import { useTranslation } from 'react-i18next'

import { cn } from '#/shared/lib/utils'
import { Button } from '#/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '#/shared/ui/dialog'

interface CampaignModalShellProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description: string
  children: React.ReactNode
  submitLabel: string
  onSubmit?: () => void
  submitDisabled?: boolean
  /** A line beside the buttons — the enrolment modals use it to say the one
   *  thing people ask at that point: no, you have not picked a channel yet. */
  footerNote?: string
  /** Narrower for a dialog that asks one question. */
  size?: 'default' | 'sm'
}

/**
 * The frame the campaign modals share: header, scrolling body, and a footer
 * with the primary action.
 */
export function CampaignModalShell({
  open,
  onOpenChange,
  title,
  description,
  children,
  submitLabel,
  onSubmit,
  submitDisabled = false,
  footerNote,
  size = 'default',
}: CampaignModalShellProps) {
  const { t } = useTranslation()
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'rounded-2xl border border-gray-200',
          size === 'sm' ? 'max-w-110' : 'max-w-140',
        )}
      >
        <header className="flex shrink-0 flex-col gap-1.5 px-6 pt-6 pb-5">
          <DialogTitle className="text-xl leading-6.5 font-semibold text-announce-ink">
            {title}
          </DialogTitle>
          <DialogDescription className="text-xs leading-4.5 text-warm-500">
            {description}
          </DialogDescription>
        </header>
        <div className="h-px w-full shrink-0 bg-gray-200" />
        {/* The body is the only scrolling part, so the footer's primary action
            stays reachable however long the options run. */}
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-5">
          {children}
        </div>
        <div className="h-px w-full shrink-0 bg-gray-200" />
        <footer className="flex shrink-0 flex-wrap items-center gap-2.5 px-6 py-4">
          {footerNote != null && (
            <p className="text-xs leading-4 text-warm-500">{footerNote}</p>
          )}
          <span className="hidden h-px flex-1 sm:block" />
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            {t('campaigns.modal.cancel')}
          </Button>
          <Button
            size="sm"
            pressEffect
            disabled={submitDisabled}
            onClick={onSubmit}
          >
            {submitLabel}
          </Button>
        </footer>
      </DialogContent>
    </Dialog>
  )
}
